import { useState, useEffect } from "react";
import { api } from "../../context";

export default function Revenue({ t }) {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const CARD=t.card,BORDER=t.border,TEXT=t.text,SUB=t.sub,PRI=t.pri,GRN=t.grn,RED=t.red,GOLD=t.gold;
  const fmt = (n) => Number(n || 0).toLocaleString();

  useEffect(() => {
    Promise.all([
      api("/admin/dashboard"),
      api("/admin/revenue"),
    ]).then(([dash, rev]) => {
      if (dash.ok) setData(dash.data);
      if (rev.ok) setPayments(rev.data || {});
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ padding: 40, color: SUB, textAlign: "center" }}>⏳ Loading revenue data...</div>;

  const monthly = payments.monthly || [];
  const byType  = payments.by_type || [];
  const maxRev  = Math.max(...monthly.map(m => m.total || 0), 1);

  const premiumMRR = (data?.plans?.premium || 0) * 499;
  const eliteMRR   = (data?.plans?.elite || 0) * 999;

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, marginBottom: 4 }}>💰 Revenue</div>
      <div style={{ fontSize: 12, color: SUB, marginBottom: 24 }}>All payments and subscription revenue</div>

      {/* Top banner */}
      <div style={{ background: "linear-gradient(135deg, #1a4a8a, #2563eb, #4f8ef7)", borderRadius: 14, padding: "24px 32px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Total Revenue Collected</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: "#fff" }}>KES <span style={{ color: "#4ade80" }}>{fmt(data?.revenue)}</span></div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 6 }}>Premium MRR: KES {fmt(premiumMRR)} · Elite MRR: KES {fmt(eliteMRR)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Failed (Recoverable)</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#fca5a5" }}>KES {fmt(data?.failed_amount)}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{data?.failed_payments || 0} failed transactions</div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "This Week", value: `KES ${fmt(data?.revenue_week)}`, color: GRN },
          { label: "Today", value: `KES ${fmt(data?.revenue_today)}`, color: PRI },
          { label: "Failed Count", value: data?.failed_payments || 0, color: RED },
          { label: "Total MRR", value: `KES ${fmt(premiumMRR + eliteMRR)}`, color: GOLD },
        ].map((s, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* MRR breakdown */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Monthly Recurring Revenue (MRR)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
          {[
            { label: "Premium MRR", value: premiumMRR, sub: `${data?.plans?.premium || 0} × KES 499`, color: GRN },
            { label: "Elite MRR", value: eliteMRR, sub: `${data?.plans?.elite || 0} × KES 999`, color: GOLD },
            { label: "Total MRR", value: premiumMRR + eliteMRR, sub: "if all renew", color: PRI },
          ].map((m, i) => (
            <div key={i} style={{ padding: "16px 24px", borderRight: i < 2 ? `1px solid ${BORDER}` : "none", textAlign: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{m.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: m.color }}>KES {fmt(m.value)}</div>
              <div style={{ fontSize: 11, color: SUB, marginTop: 4 }}>{m.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly chart */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Monthly Revenue</div>
        {monthly.length === 0
          ? <div style={{ textAlign: "center", padding: "30px 0", color: SUB }}>No monthly data yet</div>
          : (
            <>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120, marginBottom: 8 }}>
                {monthly.slice().reverse().map((m, i) => {
                  const h = Math.max(4, (m.total / maxRev) * 110);
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ fontSize: 9, color: SUB }}>{fmt(m.total)}</div>
                      <div style={{ width: "100%", height: h, background: `linear-gradient(180deg, ${GRN}, ${GRN}60)`, borderRadius: "4px 4px 0 0" }} title={`${m.month}: KES ${fmt(m.total)}`} />
                      <div style={{ fontSize: 9, color: SUB, whiteSpace: "nowrap" }}>{m.month?.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 16 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${BORDER}` }}>
                    {["Month", "Revenue (KES)", "Transactions"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: SUB, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((m, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <td style={{ padding: "10px 12px", color: TEXT, fontWeight: 600 }}>{m.month}</td>
                      <td style={{ padding: "10px 12px", fontWeight: 800, color: GRN }}>KES {fmt(m.total)}</td>
                      <td style={{ padding: "10px 12px", color: SUB }}>{m.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )
        }
      </div>

      {/* Revenue by type */}
      {byType.length > 0 && (
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Revenue by Type</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
            {byType.map((t2, i) => (
              <div key={i} style={{ background: t.input, borderRadius: 12, padding: 16, textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: GRN }}>KES {fmt(t2.total)}</div>
                <div style={{ fontSize: 12, color: SUB, marginTop: 4, textTransform: "capitalize" }}>{t2.type} ({t2.count})</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily revenue chart */}
      {data?.revenue_by_day?.length > 0 && (
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Daily Revenue (30 days)</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 80 }}>
            {data.revenue_by_day.map((d, i) => {
              const max = Math.max(...data.revenue_by_day.map(x => x.total || 0), 1);
              const h = Math.max(2, ((d.total || 0) / max) * 76);
              return <div key={i} style={{ flex: 1, height: h, background: GRN, borderRadius: "2px 2px 0 0", opacity: 0.8, minWidth: 3 }} title={`${d.date}: KES ${fmt(d.total)}`} />;
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ fontSize: 10, color: SUB }}>{data.revenue_by_day[0]?.date?.slice(5)}</span>
            <span style={{ fontSize: 10, color: SUB }}>{data.revenue_by_day[data.revenue_by_day.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}

      {/* Failed payments */}
      {data?.failed_by_day?.length > 0 && (
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Failed Payments (30 days) — KES {fmt(data.failed_amount)} recoverable</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 60 }}>
            {data.failed_by_day.map((d, i) => {
              const max = Math.max(...data.failed_by_day.map(x => x.count || 0), 1);
              const h = Math.max(2, (d.count / max) * 56);
              return <div key={i} style={{ flex: 1, height: h, background: RED, borderRadius: "2px 2px 0 0", opacity: 0.8, minWidth: 3 }} title={`${d.date}: ${d.count} failed`} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}